import * as RadixDialog from '@radix-ui/react-dialog';
import type { CSSProperties, ReactNode } from 'react';

import { istAusSchlafecke } from '../features/schlaf/schlafecke';
import { istAusBuehne } from './buehne';
import styles from './Dialog.module.css';

/**
 * Mittiges Blatt über dem Bogen, auf den Radix-Primitiven.
 *
 * Radix bringt das mit, was von Hand regelmäßig fehlt: Fokus bleibt im Blatt gefangen und kehrt
 * beim Schließen an den auslösenden Knopf zurück, Escape schließt, der Bogen dahinter lässt sich
 * nicht scrollen, und die Vorleseprogramme bekommen `dialog`/`aria-modal` samt Titel. Optik und
 * Verhalten bleiben getrennt: Radix schreibt keine einzige Farbe vor.
 */
export function Dialog({
  kicker,
  title,
  onClose,
  children,
  width = 560,
  dismissible = true,
}: {
  kicker: string;
  /** Darf Auszeichnung tragen (`HtmlText`), etwa der Titel einer Aufgabe. */
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  /**
   * Ob Escape und ein Tippen daneben schliessen. Die Blätter des Startablaufs setzen es auf
   * `false`: dahinter liegt nichts, was sich bedienen liesse, und ein versehentlich
   * weggetipptes Blatt liesse den Prüfer vor einem Bildschirm ohne Ausweg stehen.
   */
  dismissible?: boolean;
}) {
  /** Escape trägt kein Ziel — hier zählt nur, ob das Blatt sich überhaupt wegtippen lässt. */
  const abwehrenEscape = (e: { preventDefault: () => void }) => {
    if (!dismissible) e.preventDefault();
  };

  /**
   * Ein Tipper daneben. Zwei Gründe, ihn abzufangen: der Dialog lässt sich gar nicht
   * wegtippen — oder er kam aus der Schlafecke, und die ist Bedienung und kein Weg nach
   * draussen (s. `features/schlaf/schlafecke.ts`).
   */
  const abwehrenDaneben = (e: { detail: { originalEvent: Event }; preventDefault: () => void }) => {
    const ziel = e.detail.originalEvent.target;
    if (!dismissible || istAusSchlafecke(ziel) || istAusBuehne(ziel)) e.preventDefault();
  };
  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && dismissible && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        <RadixDialog.Content
          className={styles.content}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={abwehrenEscape}
          onPointerDownOutside={abwehrenDaneben}
          onInteractOutside={abwehrenDaneben}
          style={{ '--dialog-width': width + 'px' } as CSSProperties}
        >
          <div className={styles.kicker}>{kicker}</div>
          <RadixDialog.Title className={styles.title}>{title}</RadixDialog.Title>
          {/* Ohne Beschreibung warnt Radix; der Titel trägt hier die ganze Aussage. */}
          <RadixDialog.Description hidden>{kicker}</RadixDialog.Description>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Der Fußknopf, den mehrere Blätter teilen (Wahl aufheben, Eingriff zurücknehmen). */
export function DialogFooterButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`tap ${styles.footerButton}`} onClick={onClick}>
      {children}
    </button>
  );
}
